<template>
  <ScaleLoader v-if="loading" />
  <div v-else>
    <ExportButtons
      :csv="exportGridsToExcel"
      :pdf="exportGridsToPdf"
      :xlsx="exportGridsToExcel"
    />
    <div class="row">
      <div class="col-sm-12">
        <Card
          :badges="1"
          :info-badge-description="[cardStore[0]]"
          :info-badge-title="[$t('components.reports.geofencing.totalEvents')]"
          :status="$t('components.reports.geofencing.geofencingReport')"
          icon="bi bi-geo-alt"
        />
      </div>
    </div>
    <div class="row">
      <DxDataGrid
        :ref="dataGridRef"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        :data-source="store"
        :hover-state-enabled="true"
        :remote-operations="true"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
      >
        <DxHeaderFilter :visible="false" />
        <DxPaging :page-size="10" />
        <DxPager
          :allowed-page-sizes="[10, 15, 20, 50]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn
          :caption="$t('components.reports.geofencing.registrationNumber')"
          data-field="vehicle_info"
          cell-template="vehicleTemplate"
          :allow-filtering="false"
          :allow-sorting="false"
          :calculate-cell-value="() => vehicle?.registration || 'N/A'"
        />
        <DxColumn
          :caption="$t('components.reports.geofencing.eventType')"
          data-field="event_type"
          cell-template="eventTypeTemplate"
          :allow-filtering="false"
          :allow-sorting="false"
          :calculate-cell-value="(rowData) => rowData.event_type === 'in' ? this.$t('components.reports.geofencing.zoneEntry') : this.$t('components.reports.geofencing.zoneExit')"
        />
        <DxColumn
          :caption="$t('components.reports.geofencing.eventDate')"
          data-field="event_date"
          :allow-filtering="false"
          :allow-sorting="true"
        />
        <DxColumn
          :caption="$t('components.reports.geofencing.zoneName')"
          data-field="zone_name"
          :allow-filtering="true"
          :allow-sorting="false"
        />
        
        <template #vehicleTemplate="{ data }">
          <div>
            <div class="fw-bold">
              {{ vehicle?.registration || 'N/A' }}
            </div>
            <div class="text-muted small">
              {{ imei }}
            </div>
          </div>
        </template>
        
        <template #eventTypeTemplate="{ data }">
          <span 
            :class="{
              'badge badge-success fw-bolder fs-8 py-1': data.data.event_type === 'in',
              'badge badge-primary fw-bolder fs-8 py-1': data.data.event_type === 'out'
            }"
          >
            {{ data.data.event_type === 'in' ? $t('components.reports.geofencing.zoneEntry') : $t('components.reports.geofencing.zoneExit') }}
          </span>
        </template>
      </DxDataGrid>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import Card from '@/components/extras/Card.vue'
import { getGeofencingReport } from '@/crud/getters'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import DataSource from 'devextreme/data/data_source'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { getHumanDateTimeFromDateTime } from '@/utils/tools'
import ExportButtons from '@/components/content/_partials/ExportButtons.vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter'
import { importFont } from '@/utils/jspdf/importFont'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import 'jspdf-autotable'

export default defineComponent({
  name: 'Geofencing',
  components: {
    ExportButtons,
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    Card,
    ScaleLoader
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object,
    reportType: String
  },
  data(): {
    store: any,
    cardStore: Array<any>,
    dataGridRef: string,
    loading: boolean,
    totalCount: number
  } {
    return {
      store: null,
      cardStore: [],
      dataGridRef: 'geofencingGrid',
      loading: true,
      totalCount: 0
    }
  },
  computed: {
    geofencingGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef]?.instance
    }
  },
  methods: {
    initializeStore() {
      if (!this.imei || !this.dateFrom || !this.dateTo) return
      
      this.store = new DataSource({
        load: (loadOptions: any) => {
          const params: any = {
            date_from: this.dateFrom,
            date_to: this.dateTo,
            page: (loadOptions.skip || 0) / (loadOptions.take || 5) + 1,
            limit: loadOptions.take || 5,
            sort_by: loadOptions.sort?.[0]?.selector === 'event_date' ? 'created_at' : (loadOptions.sort?.[0]?.selector || 'created_at'),
            sort_order: loadOptions.sort?.[0]?.desc ? 'desc' : 'asc'
          }
          
          // Add zone name filter if provided and has at least 3 characters
          if (loadOptions.filter) {
            const zoneFilter = this.extractZoneFilter(loadOptions.filter)
            if (zoneFilter && zoneFilter.length >= 3) {
              params.zone_name = zoneFilter
            }
          }
          
          return getGeofencingReport(
            this.imei as bigint,
            params.date_from,
            params.date_to,
            params.page,
            params.limit,
            params.zone_name,
            params.sort_by,
            params.sort_order
          ).then((response) => {
            const data = response.data
            this.totalCount = data.pagination?.total || 0
            this.cardStore = [this.totalCount]
            
            const items = (data.geofencing_events || []).map((item: any) => ({
              ...item,
              event_date: getHumanDateTimeFromDateTime(item.event_date)
            }))
            
            return {
              data: items,
              totalCount: data.pagination?.total || 0
            }
          }).catch((error) => {
            const self: any = this
            self.$swal.fire({
              icon: 'warning',
              title: 'Oops...',
              text: this.$t('components.error')
            })
            return {
              data: [],
              totalCount: 0
            }
          })
        }
      })
      
      this.loading = false
    },
    
    extractZoneFilter(filter: any): string | null {
      // Extract zone_name filter from DevExtreme filter array
      if (Array.isArray(filter)) {
        for (const item of filter) {
          if (Array.isArray(item) && item[0] === 'zone_name') {
            return item[2] // filter value
          } else if (Array.isArray(item)) {
            const result = this.extractZoneFilter(item)
            if (result) return result
          }
        }
      }
      return null
    },
    
    exportGridsToExcel(extension: string) {
      const self: any = this
      const workbook = new Workbook()
      const sheet = workbook.addWorksheet(self.$t('components.reports.geofencing.geofencingReport'))

      exportDataGrid({
        worksheet: sheet,
        component: self.geofencingGridInstance
      }).then(() => {
        if (extension == 'csv') {
          workbook.csv.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                `Geofencing_report_${self.imei}.csv`)
          })
        } else if (extension == 'xlsx') {
          workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                `Geofencing_report_${self.imei}.xlsx`)
          })
        }
      })
    },
    
    exportGridsToPdf() {
      const doc = new jsPDF()
      importFont(doc)

      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: this.geofencingGridInstance
      }).then(() => {
        doc.save(`Geofencing_report_${this.imei}.pdf`)
      })
    }
  },
  mounted() {
    this.initializeStore()
  },
  watch: {
    imei() {
      this.initializeStore()
    },
    dateFrom() {
      this.initializeStore()
    },
    dateTo() {
      this.initializeStore()
    }
  }
})
</script>

<style lang="scss" scoped>
.btn-custom {
  display: block;
  margin: 0 0 0 auto;
}

button {
  i {
    font-size: 2rem;
  }
}

.form-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.form-control, .form-select {
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75rem;
}

.text-muted {
  color: #6c757d !important;
}

.small {
  font-size: 0.75rem;
}

.fw-bold {
  font-weight: 600;
}
</style>